import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import QRCode from "react-qr-code";
import cx from "classnames";
import Header from "../Header/header";
import styles from "./summary.module.scss";
import { useEffect } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import SummaryTutorialOverlay, {
  SummaryTutorialStep,
} from "./SummaryTutorialOverlay";

import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";

import SignatureCanvas from "react-signature-canvas";

/* ----------------------------- Types (minimal) ----------------------------- */
type Patient = {
  title?: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  mobile_phone?: string;
  home_phone?: string;
  address_line_1?: string;
  address_line_2?: string;
  nhs_number?: string;
};

type TreatmentPlan = {
  treatments?: { description: string }[];
};

type PlaylistItem = {
  id: string | number;
  title?: string;
  thumbnail?: string;
  status?: boolean;
};

type Playlist = {
  title?: string;
  status?: boolean;
  playlist_items?: PlaylistItem[];
};

type PlaylistActivityRow = {
  video_id: string | number;
  watch_percentage: number; // 0..1
  watch_status: "Not Watched" | "In Progress" | "Completed" | string;
  last_watched?: string | null;
  has_playback_issue?: boolean;
};

type SignatureMeta = {
  signed_type?: "manual" | "digital";
  signed_timestamp?: string | null;
};

type ClinicianSignatureMeta = {
  signed_timestamp?: string | null;
  signed_type?: "manual" | "digital" | string;
};

type SummaryDTO = {
  id: string | number;
  version_no: number;
  created_at: string;
  status: "Draft" | "Sent" | "Completed" | string;
  is_old_version?: boolean;
  link?: string;
  patient?: Patient;
  treatment_plan?: TreatmentPlan;
  clinician_table_id?: string | number;
  playlists?: Playlist[];
  playlist_activity?: PlaylistActivityRow[];
  patient_signature?: SignatureMeta;
  clinician_signature?: ClinicianSignatureMeta | null;
};

type VersionsRow = {
  consent_bundle_id: string;
  code: string;
  version: number;
};

type ComplaintStatus = "pending" | "resolved" | string;

type ComplaintRecord = {
  id: number | string;
  consent_bundle_id: number | string;
  complaint_body?: {
    issue?: string;
    details?: string;
    [key: string]: any;
  };
  status: ComplaintStatus;
};

/* ---------------------------- Utility functions ---------------------------- */
const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toDateString() : "-";
const percent = (x: number) =>
  `${(Math.min(Math.max(x, 0), 1) * 100).toFixed(0)}%`;

/* ------------------------------- Component -------------------------------- */
export default function Summary() {
  const { consentBundleId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<
    "CONSENT" | "WAITING" | "AUDIT" | "DOCS"
  >("CONSENT");
  const [showSummaryTutorial, setShowSummaryTutorial] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [versionList, setVersionList] = useState<VersionsRow[]>([]);
  const user: any = queryClient.getQueryData(["user"]);

  const summaryTutorialSteps = useMemo<SummaryTutorialStep[]>(
    () => [
      {
        id: "summary-overview",
        selector: ".summary-tutorial-topbar",
        title: "Consent summary overview",
        text: "This top section shows the current consent status, created date, version, consent bundle ID, treatment plan ID, and treatment summary.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-download",
        selector: ".summary-tutorial-download",
        title: "Download the consent document",
        text: "Use this button to download the latest consent document for this bundle.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-preview",
        selector: ".summary-tutorial-preview",
        title: "Preview before downloading",
        text: "Preview opens the latest generated consent PDF in a new tab so you can review it before saving or printing.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-new-version",
        selector: ".summary-tutorial-new-version",
        title: "Update as a new version",
        text: "If the consent needs changes, create a new version instead of editing the sent version directly.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-tabs",
        selector: ".summary-tutorial-tabs",
        title: "Summary sections",
        text: "Use these tabs to move between patient details, waiting room signing options, audit log, and documents/downloads.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-secure-link",
        selector: ".summary-tutorial-secure-link",
        title: "Patient secure link",
        text: "This is the secure link the patient uses to open and complete the consent remotely.",
        placement: "bottom",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-patient-details",
        selector: ".summary-tutorial-patient-card",
        title: "Patient details",
        text: "Check the patient name, email, phone, address, and NHS number before sending or discussing the consent.",
        placement: "top",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-consent-status",
        selector: ".summary-tutorial-consent-status",
        title: "Consent status",
        text: "This card shows whether the consent is completed, sent for signing, or still unsigned. You can also print or mark manual signing from here when needed.",
        placement: "right",
        beforeShow: () => setActiveTab("CONSENT"),
      },
      {
        id: "summary-waiting-room-tab",
        selector: ".summary-tutorial-waiting-tab",
        title: "Waiting room signing options",
        text: "The waiting room tab contains manual signing, remote link sharing, and QR-code signing options.",
        placement: "bottom",
        beforeShow: () => setActiveTab("WAITING"),
      },
      {
        id: "summary-manual-signing",
        selector: ".summary-tutorial-manual-sign",
        title: "Manual signing at the practice",
        text: "Print a physical consent form for the patient to sign manually. The printed version does not include the digital audit log.",
        placement: "bottom",
        beforeShow: () => setActiveTab("WAITING"),
      },
      {
        id: "summary-remote-signing",
        selector: ".summary-tutorial-remote-sign",
        title: "Sign remotely",
        text: "Copy the secure link if you want to manually send it to the patient by email or text.",
        placement: "top",
        beforeShow: () => setActiveTab("WAITING"),
      },
      {
        id: "summary-qr-code",
        selector: ".summary-tutorial-qr-sign",
        title: "Sign in the waiting room",
        text: "Generate a QR code so the patient can scan it in the waiting room and open their consent documents.",
        placement: "top",
        beforeShow: () => setActiveTab("WAITING"),
      },
      {
        id: "summary-audit-tab",
        selector: ".summary-tutorial-audit-tab",
        title: "Audit log",
        text: "The audit log shows key patient-consent activity such as links being sent, opened, signed, or other recorded interactions.",
        placement: "bottom",
        beforeShow: () => setActiveTab("AUDIT"),
      },
      {
        id: "summary-audit-log",
        selector: ".summary-tutorial-audit-log",
        title: "Review audit events",
        text: "Use this timeline to review what happened and when. It helps you understand the patient journey and consent activity.",
        placement: "top",
        beforeShow: () => setActiveTab("AUDIT"),
      },
      {
        id: "summary-docs-tab",
        selector: ".summary-tutorial-docs-tab",
        title: "Documents and downloads",
        text: "This tab contains document versions, report downloads, and complaint-related record tools.",
        placement: "bottom",
        beforeShow: () => setActiveTab("DOCS"),
      },
      {
        id: "summary-documents",
        selector: ".summary-tutorial-docs-card",
        title: "Document versions",
        text: "Review available document versions here. If multiple versions exist, you can download the version you need.",
        placement: "top",
        beforeShow: () => setActiveTab("DOCS"),
      },
      {
        id: "summary-complaint",
        selector: ".summary-tutorial-complaint",
        title: "Patient complaint flag",
        text: "This area is reserved for complaint handling. When enabled, it can help flag the case and prompt full report download for records.",
        placement: "top",
        beforeShow: () => setActiveTab("DOCS"),
      },
      {
        id: "summary-dashboard-return",
        selector: ".summary-tutorial-dashboard-button",
        title: "Return to dashboard",
        text: "When you finish reviewing the summary, click here to go back to the dashboard.",
        placement: "left",
        beforeShow: () => setActiveTab("DOCS"),
      },
    ],
    [setActiveTab],
  );
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewId, setQuickViewId] = useState<string | number | null>(null);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewError, setQuickViewError] = useState("");
  const [interactionLog, setInteractionLog] = useState<any>({});

  const [copiedToast, setCopiedToast] = useState(false);
  const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [complaintIssue, setComplaintIssue] = useState("");
  const [complaintDetails, setComplaintDetails] = useState("");

  const [clinicianId, setClinicianId] = useState<string | number | "">("");
  const [clinicianSign, setClinicianSign] = useState(false);
  const [checkAccessRights, setCheckAccessRights] = useState<{
    is_owner: boolean;
    can_edit: boolean;
  }>({ is_owner: true, can_edit: true });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeInput, setActiveInput] = useState<"signature" | "name">(
    "signature",
  );
  const [userFullName, setUserFullName] = useState("");
  const [isSignatureCaptured, setIsSignatureCaptured] = useState(false);
  const [isSignatureApplied, setIsSignatureApplied] = useState(false);

  const mySignature = React.useRef<SignatureCanvas | null>(null);
  const isMobile = window.innerWidth <= 768;

  const summaryQuery = useQuery(
    ["summaryDetails", consentBundleId],
    async () => {
      const { data } = await axios.get<SummaryDTO>(
        `/consent_bundle/${consentBundleId}/activity_summary`,
      );

      if (data.status === "Draft" && (data as any)?.data?.treatment_plan_id) {
        navigate(`/${(data as any).data.treatment_plan_id}`);
      }

      return data;
    },
  );

  const data = summaryQuery.data;
  const patient = data?.patient;

  const complaintQuery = useQuery<ComplaintRecord | null>(
    ["complaintDetails", consentBundleId],
    async () => {
      try {
        const res = await axios.get<ComplaintRecord>(
          complaintApiUrl(`/complaint_management/complaint/${consentBundleId}`),
        );
        return res.data;
      } catch (error: any) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    },
    {
      enabled: activeTab === "DOCS" && !!consentBundleId,
      retry: false,
    },
  );

  const complaint = complaintQuery.data;

  /* ------------------------------- Apply My Signature ------------------------------ */

  const complaintApiUrl = (path: string) => {
    const baseUrl = process.env.REACT_APP_BASE_URL_V2?.replace(/\/$/, "") || "";
    return `${baseUrl}${path}`;
  };

  useEffect(() => {
    if (!data) return;

    if (
      data.clinician_table_id !== undefined &&
      data.clinician_table_id !== null
    ) {
      setClinicianId(data.clinician_table_id);
    }

    if (data.clinician_signature?.signed_timestamp) {
      setClinicianSign(true);
    } else {
      setClinicianSign(false);
    }
  }, [data]);

  // fetch access rights
  useEffect(() => {
    if (!consentBundleId) return;

    const fetchAccessRights = async () => {
      try {
        const res = await axios.get(
          `/consent_bundle/${consentBundleId}/check_access_rights`,
        );
        setCheckAccessRights(res.data);
      } catch (error) {
        console.error("Failed to fetch access rights:", error);
      }
    };

    fetchAccessRights();
  }, [consentBundleId]);

  const eraseSignature = () => {
    setIsSignatureCaptured(false);
    mySignature.current?.clear();
  };

  const markAsSignedByPractitioner = async () => {
    await axios.post(
      `/consent_bundle/${consentBundleId}/mark_as_signed_by_practitioner`,
      null,
      {
        headers: {
          Authorization: axios.defaults.headers.common.Authorization,
        },
      },
    );

    queryClient.invalidateQueries({ queryKey: ["summaryDetails"] });
  };

  const signLegacy = useMutation(
    async ({ image, name }: { image?: string; name?: string }) => {
      const token = axios.defaults.headers.common.Authorization;

      const formattedData = image ? { image } : { name };

      return axios.post(
        `/practitioner/${clinicianId}/save_signature`,
        formattedData,
        {
          headers: { Authorization: token },
        },
      );
    },
    {
      onSuccess: async () => {
        setIsModalOpen(false);
        setIsSignatureApplied(true);
        await markAsSignedByPractitioner();
      },
    },
  );

  const handleModalOpen = async () => {
    try {
      if (!clinicianId) {
        console.error("Missing clinicianId (clinician_table_id)");
        return;
      }

      const res = await axios.get(
        `/practitioner/${clinicianId}/check_if_signature_exists`,
      );
      const signatureExists = res.data;

      if (!signatureExists) {
        // open modal to capture signature
        setActiveInput("signature");
        setIsSignatureCaptured(false);
        setIsModalOpen(true);
        return;
      }

      // signature exists -> just apply it
      await markAsSignedByPractitioner();
      setIsSignatureApplied(true);
    } catch (error) {
      console.error("Error checking clinician signature:", error);
    }
  };

  const handleSignatureSave = () => {
    if (!mySignature.current) return;
    const signatureData = mySignature.current.toDataURL();
    signLegacy.mutate({ image: signatureData });
  };

  const handleNameSave = () => {
    if (!userFullName) return;
    signLegacy.mutate({ name: userFullName });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setActiveInput("signature");
    setUserFullName("");
    eraseSignature();
  };
  /* ------------------------------- Apply My Signature ------------------------------ */

  /* ------------------------------- Data load ------------------------------ */
  // const summaryQuery = useQuery(["summaryDetails", consentBundleId], async () => {
  //   const { data } = await axios.get<SummaryDTO>(`/consent_bundle/${consentBundleId}/activity_summary`);
  //   // Keep your existing behavior
  //   if (data.status === "Draft" && (data as any)?.data?.treatment_plan_id) {
  //     navigate(`/${(data as any).data.treatment_plan_id}`);
  //   }
  //   return data;
  // });

  // const data = summaryQuery.data;
  // const patient = data?.patient;

  /* ------------------------------ Mutations ------------------------------ */
  const manuallySign = useMutation(
    async (id: string | number) =>
      axios.put(`/consent_bundle/${id}/mark_consent_bundle_as_signed`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["summaryDetails"] });
      },
    },
  );

  const openConsentBundle = async (bundleId: string | number) => {
    try {
      const res = await axios.get(`/consent_bundle/${bundleId}`);
      const bundle = res.data;

      if (!bundle?.status) {
        toast.error("No status found for this consent bundle");
        return;
      }

      const status = String(bundle.status).toLowerCase();

      if (["completed", "signed", "expired"].includes(status)) {
        navigate(`/summary-new/${bundleId}`);
        return;
      }

      if (status === "draft") {
        sessionStorage.setItem("activeTreatmentData", JSON.stringify(bundle));
        navigate(`/new-consentBundle?step=1&consentBundleId=${bundleId}`);
        return;
      }

      if (
        status.includes("sent") ||
        status.includes("unsigned") ||
        status.includes("sign")
      ) {
        navigate(`/summary-new/${bundleId}`);
        return;
      }

      toast.error(`Cannot open — this consent is ${bundle.status}`);
    } catch (err) {
      console.error("Failed to fetch consent bundle:", err);
      toast.error("Error loading consent bundle");
    }
  };

  const createNewVersion = useMutation(
    async (id: string | number) =>
      axios.post(`/consent_bundle/${id}/create_consent_bundle_new_version`),
    {
      onSuccess: async (res) => {
        const newId =
          res.data?.consent_bundle_id ??
          res.data?.id ??
          res.data?.data?.id ??
          res.data?.data?.consent_bundle_id ??
          res.data;

        if (!newId) {
          toast.error(
            "Could not create new consent bundle version (missing new id).",
          );
          return;
        }

        await openConsentBundle(newId);
      },
      onError: (err) => {
        console.error("Failed to create new version:", err);
        toast.error("Failed to create new version");
      },
    },
  );

  const resetComplaintForm = () => {
    setComplaintIssue("");
    setComplaintDetails("");
  };

  const createComplaint = useMutation(
    async () =>
      axios.post(complaintApiUrl(`/complaint_management/${consentBundleId}`), {
        complaint_body: {
          issue: complaintIssue.trim(),
          details: complaintDetails.trim(),
        },
      }),
    {
      onSuccess: async () => {
        toast.success("Patient complaint flagged successfully");
        setShowComplaintModal(false);
        resetComplaintForm();
        await queryClient.invalidateQueries({
          queryKey: ["complaintDetails", consentBundleId],
        });
      },
      onError: (err) => {
        console.error("Failed to create complaint:", err);
        toast.error("Failed to flag patient complaint");
      },
    },
  );

  const updateComplaintStatus = useMutation(
    async (status: "pending" | "resolved") =>
      axios.patch(
        complaintApiUrl(`/complaint_management/status/${consentBundleId}`),
        {
          status,
        },
      ),
    {
      onSuccess: async () => {
        toast.success("Complaint status updated");
        await queryClient.invalidateQueries({
          queryKey: ["complaintDetails", consentBundleId],
        });
      },
      onError: (err) => {
        console.error("Failed to update complaint status:", err);
        toast.error("Failed to update complaint status");
      },
    },
  );

  const handleCreateComplaint = () => {
    if (!complaintIssue.trim()) {
      toast.error("Please enter the complaint issue");
      return;
    }

    if (!complaintDetails.trim()) {
      toast.error("Please enter the complaint details");
      return;
    }

    createComplaint.mutate();
  };

  const handleCloseComplaintModal = () => {
    if (createComplaint.isLoading) return;
    setShowComplaintModal(false);
    resetComplaintForm();
  };

  /* --------------------------- Derived bits --------------------------- */
  const treatmentTitle = useMemo(() => {
    const list = data?.treatment_plan?.treatments || [];
    if (!list.length) return "-";
    return list.map((t) => t.description).join(", ");
  }, [data]);

  const createdOn = formatDate(data?.created_at);
  const hasMultipleVersions = (data?.version_no ?? 1) > 1;

  // activity map (kept in case you show video status elsewhere)
  const activityMap = useMemo(() => {
    const map = new Map<string | number, PlaylistActivityRow>();
    (data?.playlist_activity || []).forEach((row) =>
      map.set(row.video_id, row),
    );
    return map;
  }, [data]);

  const visiblePlaylists = (data?.playlists || []).filter((p) => p.status);
  const anyVideoIssue = useMemo(
    () => (data?.playlist_activity || []).some((r) => r.has_playback_issue),
    [data],
  );

  /* --------------------------- Action handlers --------------------------- */
  const downloadLatest = async () => {
    if (!data) return;
    setIsDownloading(true);
    try {
      const completed = data.status === "Completed";
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/${completed ? "generate_pdf" : "generate_offline_pdf"}`;

      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const previewLatest = async () => {
    if (!data) return;
    setIsDownloading(true);
    try {
      const completed = data.status === "Completed";
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/${completed ? "generate_pdf" : "generate_offline_pdf"}`;

      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);
      window.open(href, "_blank", "noopener,noreferrer");
      // don’t revoke immediately; allow browser to load it
      setTimeout(() => URL.revokeObjectURL(href), 30_000);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadFullReport = async () => {
    if (!data) return;
    setIsDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/download_full_consent_bundle_report`;
      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadVersion = async (bundleId: string) => {
    setIsDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${bundleId}/generate_pdf`;
      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);

      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", getPdfName(data || ({} as any)));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleVersionClick = async () => {
    if (!hasMultipleVersions) return;

    if (showDropdown) {
      setShowDropdown(false);
      return;
    }

    try {
      const resp = await axios.get<VersionsRow[]>(
        `/consent_bundle/${consentBundleId}/get_consent_bundle_versions`,
      );
      setVersionList(resp.data || []);
      setShowDropdown(true);
    } catch (err) {
      console.error("Error fetching versions:", err);
      setVersionList([]);
      setShowDropdown(true);
    }
  };

  const handleSelectVersion = async (bundleId: string | number) => {
    setShowDropdown(false);
    navigate(`/summary/${bundleId}`);
  };

  const copyRemoteLink = async () => {
    if (!data) return;

    const link =
      data.link || `${process.env.REACT_APP_PATIENT_URL}/${consentBundleId}`;

    await navigator.clipboard.writeText(link);

    setCopiedToast(true);

    setTimeout(() => {
      setCopiedToast(false);
    }, 1800);
  };

  const loadAuditLog = async (id: string | number) => {
    setQuickViewLoading(true);
    setQuickViewError("");
    setInteractionLog({});

    try {
      const res = await axios.get(`/consent_bundle/${id}/interaction_log`);

      // API returns an object keyed by date
      setInteractionLog(res.data || {});
    } catch (err) {
      console.error(err);
      setQuickViewError("Failed to load audit log");
    } finally {
      setQuickViewLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "AUDIT" && consentBundleId) {
      loadAuditLog(consentBundleId);
    }
  }, [activeTab, consentBundleId]);

  const versionStatusLabel = (v: VersionsRow, latestVersion: number) => {
    if (v.version === latestVersion) return "Current";
    if (v.version < latestVersion) return "Archived";
    return "Signed";
  };

  useEffect(() => {
    if (activeTab === "DOCS" && consentBundleId) {
      handleVersionClick();
    }
  }, [activeTab]);

  const printOfflineConsent = async () => {
    if (!data) return;

    setIsDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}consent_bundle/${consentBundleId}/generate_offline_pdf`;

      const resp = await axios.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(resp.data);

      // Open print dialog
      const win = window.open(href, "_blank");
      win?.addEventListener("load", () => {
        win.print();
      });

      setTimeout(() => URL.revokeObjectURL(href), 30_000);
    } finally {
      setIsDownloading(false);
    }
  };

  // Show the print/download button ONLY after manual signing is done
  const isManualSigned = data?.patient_signature?.signed_type === "manual";

  const handleMarkManualSigned = async () => {
    if (!data) return;
    try {
      await manuallySign.mutateAsync(data.id);
      setShowPrintConfirm(false);
      await queryClient.invalidateQueries({ queryKey: ["summaryDetails"] });
    } catch (err) {
      console.error("Mark manual signed failed", err);
    }
  };

  /* -------------------------------- Render -------------------------------- */
  return (
    <>
      <Header />

      <div className={styles.page}>
        <div className={styles.headerBlock}>
          <div className={styles.headerText}>
            <h1 className={styles.pageTitle}>Summary</h1>
            <p className={styles.pageSubtitle}>
              Here’s a summary of your patient’s consent journey
            </p>
          </div>

          <button
            type="button"
            className={styles.tutorialInfoButton}
            onClick={() => setShowSummaryTutorial(true)}
            title="Show tutorials"
            aria-label="Show tutorials"
          >
            i
          </button>
        </div>

        {/* Top orange bordered summary bar (like screenshot) */}
        <div className={cx(styles.topBar, "summary-tutorial-topbar")}>
          <div className={styles.topBarRow}>
            <span
              className={cx(styles.statusPillTop, {
                [styles.statusCompleted]: data?.status === "Completed",
                [styles.statusOther]: data?.status !== "Completed",
              })}
            >
              {data?.status || "—"}
            </span>

            <span className={styles.sep} />

            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Created:</span>
              <span className={styles.metaValue}>{createdOn}</span>
            </div>

            <span className={styles.sep} />

            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Version:</span>
              <span className={styles.metaValue}>
                {data?.version_no ?? "-"}
              </span>
            </div>

            <span className={styles.sep} />

            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Consent bundle ID:</span>
              <span className={styles.metaValue}>{data?.id ?? "-"}</span>
            </div>

            <span className={styles.sep} />

            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Treatment plan ID:</span>
              <span className={styles.metaValue}>
                {data?.treatment_plan_id ?? "-"}
              </span>
            </div>

            <span className={styles.sep} />

            <div className={styles.metaItemTruncate} title={treatmentTitle}>
              <span className={styles.metaValue}>{treatmentTitle}</span>
            </div>
          </div>

          <div className={styles.topBarActions}>
            <button
              className={cx(styles.iconBtn, "summary-tutorial-download")}
              onClick={downloadLatest}
              disabled={isDownloading || summaryQuery.isLoading}
              title="Download"
            >
              {/* simple download icon */}
              <DownloadIcon />
            </button>

            <button
              className={cx(styles.outlineBtn, "summary-tutorial-preview")}
              onClick={previewLatest}
              disabled={isDownloading || summaryQuery.isLoading}
            >
              <span className={styles.btnIcon}>👁</span>
              Preview
            </button>

            <button
              className={cx(styles.outlineBtn, "summary-tutorial-new-version")}
              onClick={() => data && createNewVersion.mutate(data.id)}
              disabled={
                !data || createNewVersion.isLoading || !!data?.is_old_version
              }
              title={
                data?.is_old_version
                  ? "Old versions can’t be edited"
                  : "Edit as new version"
              }
            >
              <span className={styles.btnIcon}>✎</span>
              Update consent &amp; create new version
            </button>
          </div>
        </div>

        {/* Tabs row */}
        <div className={cx(styles.tabs, "summary-tutorial-tabs")}>
          <button
            className={cx(styles.tab, {
              [styles.tabActive]: activeTab === "CONSENT",
            })}
            onClick={() => setActiveTab("CONSENT")}
          >
            Consent &amp; Patient
          </button>
          <button
            className={cx(styles.tab, "summary-tutorial-waiting-tab", {
              [styles.tabActive]: activeTab === "WAITING",
            })}
            onClick={() => setActiveTab("WAITING")}
          >
            Waiting Room
          </button>
          <button
            className={cx(styles.tab, "summary-tutorial-audit-tab", {
              [styles.tabActive]: activeTab === "AUDIT",
            })}
            onClick={() => setActiveTab("AUDIT")}
          >
            Audit Log
          </button>
          <button
            className={cx(styles.tab, "summary-tutorial-docs-tab", {
              [styles.tabActive]: activeTab === "DOCS",
            })}
            onClick={() => setActiveTab("DOCS")}
          >
            Documents &amp; Downloads
          </button>
        </div>

        {showPrintConfirm && (
          <div
            className={styles.modalBackdrop}
            onClick={() => setShowPrintConfirm(false)}
          >
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h3 className={styles.modalTitle}>Print Consent Form</h3>

              <p className={styles.modalText}>
                I declare that I will present this to the patient to sign
                manually and I understand there will be no digital audit log
                attached to this version.
              </p>

              <div className={styles.modalActions}>
                <button
                  className={styles.outlineBtn}
                  onClick={() => setShowPrintConfirm(false)}
                >
                  Cancel
                </button>

                <button
                  className={styles.primaryBtn}
                  disabled={manuallySign.isLoading || isDownloading}
                  onClick={handleMarkManualSigned}
                >
                  I Understand, Mark as signed
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {activeTab === "CONSENT" && (
          <>
            {/* Patient Details */}
            <section
              className={cx(styles.card, "summary-tutorial-patient-card")}
            >
              <div
                className={cx(styles.linkBox, "summary-tutorial-secure-link")}
              >
                {data?.link ||
                  `${process.env.REACT_APP_PATIENT_URL}/${consentBundleId}`}
              </div>

              <button
                className={styles.outlineBtn}
                onClick={copyRemoteLink}
                disabled={!data}
              >
                Copy link
              </button>

              <h2 className={styles.cardTitle}>Patient details</h2>

              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label}>Name</label>
                  <input
                    className={styles.input}
                    type="text"
                    readOnly
                    value={
                      patient
                        ? `${patient.title ?? ""} ${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim()
                        : "-"
                    }
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Email address</label>
                  <input
                    className={styles.input}
                    type="text"
                    readOnly
                    value={patient?.email_address || "-"}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Mobile number</label>
                  <input
                    className={styles.input}
                    type="text"
                    readOnly
                    value={patient?.mobile_phone || "-"}
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>Phone</label>
                  <input
                    className={styles.input}
                    type="text"
                    readOnly
                    value={patient?.home_phone || "-"}
                  />
                </div>

                <div className={cx(styles.field, styles.fullWidth)}>
                  <label className={styles.label}>Address</label>
                  <input
                    className={styles.input}
                    type="text"
                    readOnly
                    value={
                      patient
                        ? `${patient.address_line_1 || ""}${patient.address_line_2 ? `, ${patient.address_line_2}` : ""}`.trim() ||
                          "-"
                        : "-"
                    }
                  />
                </div>

                <div className={styles.field}>
                  <label className={styles.label}>NHS Number</label>
                  <input
                    className={styles.input}
                    type="text"
                    readOnly
                    value={patient?.nhs_number || "-"}
                  />
                </div>
              </div>
            </section>

            {/* Consent status */}
            <section
              className={cx(
                styles.card,
                styles.cardStatus,
                styles.halfWidth,
                "summary-tutorial-consent-status",
              )}
            >
              <div className={styles.statusHeader}>
                <h2 className={styles.cardTitle}>Consent status</h2>

                <div className={styles.statusRight}>
                  <span
                    className={cx(styles.statusCircle, {
                      [styles.circleGreen]: data?.status === "Completed",
                      [styles.circleOrange]: data?.status !== "Completed",
                    })}
                  />
                  <span
                    className={cx(styles.statusText, {
                      [styles.textGreen]: data?.status === "Completed",
                      [styles.textOrange]: data?.status !== "Completed",
                    })}
                  >
                    {data?.status || "—"}
                  </span>
                </div>
              </div>

              {data?.status === "Completed" ? (
                <div className={styles.statusRows}>
                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>
                      Signed manually at the practice
                    </span>

                    <button
                      className={styles.outlineBtn}
                      onClick={printOfflineConsent}
                      disabled={isDownloading || summaryQuery.isLoading}
                      title="Print manual consent (offline PDF)"
                    >
                      🖨 Print manual consent
                    </button>
                  </div>

                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>
                      Patient signature status:
                    </span>
                    <span className={cx(styles.smallPill, styles.pillGreen)}>
                      SIGNED
                    </span>
                  </div>

                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>
                      Clinician signature status:
                    </span>
                    <span className={cx(styles.smallPill, styles.pillGreen)}>
                      SIGNED
                    </span>
                  </div>
                </div>
              ) : (
                <div className={styles.statusRows}>
                  {/* Manual sign + print */}
                  <div className={styles.statusRow}>
                    <div className={styles.statusRow}>
                      <span className={styles.statusLabel}>
                        Signed manually at the practice
                      </span>

                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <button
                          className={styles.outlineBtn}
                          onClick={printOfflineConsent}
                          disabled={isDownloading || summaryQuery.isLoading}
                          title="Print manual consent (offline PDF)"
                        >
                          🖨 Print manual consent
                        </button>

                        <button
                          className={styles.outlineBtn}
                          onClick={() => setShowPrintConfirm(true)}
                          disabled={
                            !data || manuallySign.isLoading || isManualSigned
                          }
                          title={
                            isManualSigned
                              ? "Already marked as signed manually"
                              : undefined
                          }
                        >
                          {isManualSigned
                            ? "Marked as signed"
                            : "Mark as signed manually"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Clinician signature */}
                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>
                      Clinician signature
                    </span>
                    <span className={cx(styles.smallPill, styles.pillOrange)}>
                      {data?.patient_signature?.signed_timestamp
                        ? "APPLIED"
                        : "NOT SIGNED"}
                    </span>
                  </div>
                </div>
              )}
            </section>

            <div className={styles.footer}>
              <button
                className={cx(
                  styles.outlineBtn,
                  "summary-tutorial-dashboard-button",
                )}
                onClick={() => navigate("/dashboard")}
              >
                Go to dashboard
              </button>
            </div>
          </>
        )}

        {copiedToast && (
          <div className={styles.copyToast}>
            <span className={styles.toastIcon}>✓</span>
            Link copied to clipboard
          </div>
        )}

        {activeTab === "WAITING" && (
          <>
            {/* Remote link */}

            <section
              className={cx(styles.card, "summary-tutorial-manual-sign")}
            >
              <h2 className={styles.cardTitle}>
                Signed manually at the practice & Print consent form
              </h2>
              <p className={styles.paragraph}>
                Print a physical copy of the consent form for your patient to
                sign manually in person.
                <strong>
                  {" "}
                  The printed document will not include the audit log.
                </strong>
              </p>
              <div className={styles.statusRow}>
                <span className={styles.statusLabel}>
                  Signed manually at the practice
                </span>
                <button
                  className={styles.outlineBtn}
                  onClick={printOfflineConsent}
                  disabled={!data || isDownloading}
                >
                  🖨 Print manual consent
                </button>
              </div>

              {data?.status === "Completed" ? (
                <div className={styles.statusRows}>
                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>
                      Patient signature status:
                    </span>
                    <span className={cx(styles.smallPill, styles.pillGreen)}>
                      SIGNED
                    </span>
                  </div>

                  <div className={styles.statusRow}>
                    <span className={styles.statusLabel}>
                      Clinician signature status:
                    </span>
                    <span className={cx(styles.smallPill, styles.pillGreen)}>
                      SIGNED
                    </span>
                  </div>
                </div>
              ) : (
                <div className={styles.statusRows}>
                  <div className={styles.statusRow}>
                    <button
                      className={styles.outlineBtn}
                      onClick={() => setShowPrintConfirm(true)}
                      disabled={!data || manuallySign.isLoading}
                    >
                      🖨 Mark as signed manually
                    </button>
                  </div>
                </div>
              )}
            </section>

            <section
              className={cx(styles.card, "summary-tutorial-remote-sign")}
            >
              <h2 className={styles.cardTitle}>Sign remotely</h2>
              <p className={styles.paragraph}>
                We’ve provided your patient with a secure link to view and
                complete their consent documents. If you’d like to resend the
                form manually via email or text, copy the link below.
              </p>

              <div className={styles.linkBox}>
                {data?.link ||
                  `${process.env.REACT_APP_PATIENT_URL}/${consentBundleId}`}
              </div>

              <button
                className={styles.outlineBtn}
                onClick={copyRemoteLink}
                disabled={!data}
              >
                Copy link
              </button>
            </section>

            {/* Waiting room QR */}
            <section className={cx(styles.card, "summary-tutorial-qr-sign")}>
              <h2 className={styles.cardTitle}>Sign in waiting room</h2>
              <p className={styles.paragraph}>
                Generate a QR code to display in your waiting room. When
                scanned, your patient will be directed to their consent
                documents.
              </p>

              <button
                className={styles.outlineBtn}
                onClick={() => setShowQR(true)}
                disabled={!data}
              >
                Generate QR code
              </button>

              {showQR && (
                <div
                  className={styles.qrBackdrop}
                  onClick={() => setShowQR(false)}
                >
                  <div
                    className={styles.qrModal}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className={styles.qrWrap}>
                      <QRCode
                        value={
                          data?.link ||
                          `${process.env.REACT_APP_PATIENT_URL}/${consentBundleId}`
                        }
                      />
                    </div>
                    <button
                      className={styles.outlineBtn}
                      onClick={() => setShowQR(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </section>

            <div className={styles.footer}>
              <button
                className={cx(
                  styles.outlineBtn,
                  "summary-tutorial-dashboard-button",
                )}
                onClick={() => navigate("/dashboard")}
              >
                Go to dashboard
              </button>
            </div>
          </>
        )}

        {activeTab === "DOCS" && (
          <>
            <section className={cx(styles.card, "summary-tutorial-docs-card")}>
              <div className={styles.docsHeader}>
                <h2 className={styles.cardTitle}>Document Versions</h2>

                {(!versionList || versionList.length <= 1) && (
                  <p className={styles.docsSub}>
                    This document has only one version, so no version history is
                    displayed.
                  </p>
                )}
              </div>

              {/* Versions Table */}
              {versionList && versionList.length > 1 && (
                <div className={styles.tableWrapper}>
                  <table className={styles.versionsTable}>
                    <thead>
                      <tr>
                        <th>Version</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th className={styles.alignRight}>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {versionList.map((v) => {
                        const status = versionStatusLabel(
                          v,
                          data?.version_no || 1,
                        );

                        return (
                          <tr key={v.consent_bundle_id}>
                            <td>
                              <div className={styles.versionCell}>
                                <strong>v{v.version}</strong>
                                <span className={styles.versionCode}>
                                  {v.code}
                                </span>
                              </div>
                            </td>

                            <td>{formatDate(data?.created_at)}</td>

                            <td>
                              <span
                                className={cx(styles.statusTag, {
                                  [styles.tagCurrent]: status === "Current",
                                  [styles.tagSigned]: status === "Signed",
                                  [styles.tagArchived]: status === "Archived",
                                })}
                              >
                                {status}
                              </span>
                            </td>

                            <td className={styles.alignRight}>
                              <div className={styles.actionIcons}>
                                {/* <button
                          className={styles.iconAction}
                          title="View"
                          onClick={() =>
                            navigate(`/summary/${v.consent_bundle_id}`)
                          }
                        >
                          👁
                        </button> */}

                                <button
                                  className={styles.iconAction}
                                  title="Download"
                                  onClick={() =>
                                    downloadVersion(v.consent_bundle_id)
                                  }
                                  disabled={isDownloading}
                                >
                                  ⬇
                                </button>

                                {/* <button
                          className={styles.iconAction}
                          title="Open version"
                          onClick={() =>
                            navigate(`/summary/${v.consent_bundle_id}`)
                          }
                        >
                          ⟳
                        </button> */}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Download full report */}
              {data?.version_no && data.version_no > 1 && (
                <button
                  className={styles.outlineBtn}
                  onClick={downloadFullReport}
                  disabled={isDownloading}
                >
                  Download Full Consent Report
                </button>
              )}

              <hr className={styles.divider} />

              {/* Flag patient complaint */}
              <div className={cx(styles.complaintPanel, "summary-tutorial-complaint")}>
  <div className={styles.complaintHeader}>
    <div>
      <p className={styles.complaintEyebrow}>Patient complaint</p>
      <h3 className={styles.complaintTitle}>Complaint record</h3>
      <p className={styles.complaintSubtitle}>
        Track patient complaints linked to this consent bundle and download the
        full consent report when needed.
      </p>
    </div>

    {complaint && (
      <span
        className={cx(styles.complaintStatusBadge, {
          [styles.complaintStatusPending]: complaint.status === "pending",
          [styles.complaintStatusResolved]: complaint.status === "resolved",
        })}
      >
        {String(complaint.status || "pending").toUpperCase()}
      </span>
    )}
  </div>

  {complaintQuery.isLoading && (
    <div className={styles.complaintLoadingCard}>
      Loading complaint details…
    </div>
  )}

  {!complaintQuery.isLoading && complaint && (
    <div className={styles.complaintRecordCard}>
      <div className={styles.complaintContentGrid}>
        <div className={styles.complaintInfoBox}>
          <span className={styles.complaintInfoLabel}>Issue</span>
          <p className={styles.complaintInfoText}>
            {complaint.complaint_body?.issue || "-"}
          </p>
        </div>

        <div className={styles.complaintInfoBox}>
          <span className={styles.complaintInfoLabel}>Details</span>
          <p className={styles.complaintInfoText}>
            {complaint.complaint_body?.details || "-"}
          </p>
        </div>
      </div>

      <div className={styles.complaintActions}>
  {complaint.status === "pending" && (
    <button
      className={styles.complaintResolveBtn}
      onClick={() => updateComplaintStatus.mutate("resolved")}
      disabled={updateComplaintStatus.isLoading}
    >
      {updateComplaintStatus.isLoading ? "Updating..." : "Mark as resolved"}
    </button>
  )}

  <button
    className={styles.complaintReportBtn}
    onClick={downloadFullReport}
    disabled={isDownloading}
  >
    {isDownloading ? "Downloading..." : "Download full consent report"}
  </button>
</div>
    </div>
  )}

  {!complaintQuery.isLoading && !complaint && (
    <div className={styles.noComplaintCard}>
      <div>
        <h4 className={styles.noComplaintTitle}>No complaint recorded</h4>
        <p className={styles.noComplaintText}>
          Use this if a patient has raised a complaint. This will flag the case
          and allow you to download the full consent report for your records.
        </p>
      </div>

      <button
        className={styles.flagComplaintBtn}
        onClick={() => setShowComplaintModal(true)}
        disabled={!data || createComplaint.isLoading}
      >
        Flag patient complaint
      </button>
    </div>
  )}
</div>
            </section>

            <div className={styles.footer}>
              <button
                className={styles.outlineBtn}
                onClick={() => navigate("/dashboard")}
              >
                Go to dashboard
              </button>
            </div>
          </>
        )}

        {activeTab === "AUDIT" && (
          <>
            <section className={cx(styles.card, "summary-tutorial-audit-log")}>
              <h2 className={styles.cardTitle}>Audit log</h2>

              {/* Loading */}
              {quickViewLoading && (
                <p className={styles.muted}>Loading audit log…</p>
              )}

              {/* Error */}
              {quickViewError && (
                <p className={styles.errorText}>{quickViewError}</p>
              )}

              {/* Timeline */}
              {!quickViewLoading &&
                !quickViewError &&
                Object.keys(interactionLog || {}).length > 0 && (
                  <div className={styles.auditTimeline}>
                    {Object.entries(interactionLog).map(
                      ([date, events]: any) => (
                        <div key={date} className={styles.auditDay}>
                          <div className={styles.auditDate}>{date}</div>

                          <div className={styles.auditEvents}>
                            {events.map((item: any, idx: number) => (
                              <div key={idx} className={styles.auditEvent}>
                                <div className={styles.auditDot} />

                                <div className={styles.auditContent}>
                                  <p className={styles.auditTitle}>
                                    {item.event}
                                  </p>

                                  {item.description?.length > 0 && (
                                    <ul className={styles.auditDesc}>
                                      {item.description.map(
                                        (d: string, i: number) => (
                                          <li key={i}>{d}</li>
                                        ),
                                      )}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                )}

              {!quickViewLoading &&
                !quickViewError &&
                Object.keys(interactionLog || {}).length === 0 && (
                  <p className={styles.muted}>
                    No audit log entries available.
                  </p>
                )}
            </section>

            <div className={styles.footer}>
              <button
                className={styles.outlineBtn}
                onClick={() => navigate("/dashboard")}
              >
                Go to dashboard
              </button>
            </div>
          </>
        )}

        <SummaryTutorialOverlay
          open={showSummaryTutorial}
          steps={summaryTutorialSteps}
          onClose={() => setShowSummaryTutorial(false)}
        />

        {/* Complaint modal */}
        <Modal
          open={showComplaintModal}
          onClose={handleCloseComplaintModal}
          aria-labelledby="complaint-modal-title"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "92%", sm: "680px" },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 3,
              borderRadius: "16px",
              outline: "none",
            }}
          >
            <h2 id="complaint-modal-title" style={{ marginTop: 0 }}>
              Flag Patient Complaint
            </h2>

            <p className={styles.mutedSmall}>
              Add the complaint issue and details. This will create a complaint
              record against consent bundle ID {consentBundleId}.
            </p>

            <TextField
              label="Complaint issue"
              value={complaintIssue}
              onChange={(e) => setComplaintIssue(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="Wrong treatment provided"
            />

            <TextField
              label="Complaint details"
              value={complaintDetails}
              onChange={(e) => setComplaintDetails(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              margin="normal"
              placeholder="Tooth extraction was incorrect"
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 18,
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCloseComplaintModal}
                disabled={createComplaint.isLoading}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleCreateComplaint}
                disabled={createComplaint.isLoading}
              >
                {createComplaint.isLoading ? "Saving..." : "Flag Complaint"}
              </Button>
            </div>
          </Box>
        </Modal>

        {/* Clinician signature modal (MUI) */}
        <Modal
          open={isModalOpen}
          onClose={handleModalClose}
          aria-labelledby="modal-signature"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "92%", sm: "640px" },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 3,
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            <h2 id="modal-signature" style={{ marginTop: 0 }}>
              Sign Consent
            </h2>

            {/* selector (desktop only, matches old behavior) */}
            {!isMobile && (
              <div style={{ marginBottom: 12 }}>
                <Chip
                  label="Signature"
                  variant={activeInput === "signature" ? "filled" : "outlined"}
                  onClick={() => {
                    setActiveInput("signature");
                    setIsSignatureCaptured(false);
                  }}
                  sx={{ mr: 2 }}
                />
                <Chip
                  label="Type Name"
                  variant={activeInput === "name" ? "filled" : "outlined"}
                  onClick={() => setActiveInput("name")}
                />
              </div>
            )}

            {activeInput === "signature" && (
              <div
                style={{
                  width: "100%",
                  border: "2px solid #243474",
                  margin: "10px 0",
                  padding: "10px",
                  borderRadius: "10px",
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                <SignatureCanvas
                  penColor="#243474"
                  ref={mySignature}
                  canvasProps={{
                    width: isMobile
                      ? Math.min(window.innerWidth * 0.85, 420)
                      : 600,
                    height: isMobile ? 220 : 320,
                    style: { width: "100%", height: "100%" },
                  }}
                  onBegin={() => setIsSignatureCaptured(true)}
                />
              </div>
            )}

            {activeInput === "name" && (
              <TextField
                label="Full Name"
                variant="outlined"
                fullWidth
                value={userFullName}
                onChange={(e) => setUserFullName(e.target.value)}
                sx={{ mt: 1 }}
              />
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 12,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleModalClose}
              >
                Close
              </Button>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {activeInput === "signature" && (
                  <>
                    <IconButton aria-label="erase" onClick={eraseSignature}>
                      <DeleteIcon />
                    </IconButton>

                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleSignatureSave}
                      disabled={!isSignatureCaptured || signLegacy.isLoading}
                    >
                      Save Signature
                    </Button>
                  </>
                )}

                {activeInput === "name" && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleNameSave}
                    disabled={!userFullName || signLegacy.isLoading}
                  >
                    Save Name
                  </Button>
                )}
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  );
}

/* -------------------------- Local helper (name) -------------------------- */
function getPdfName(s: SummaryDTO | any) {
  const patient = s?.patient;
  const name = patient
    ? `${patient.first_name || ""}_${patient.last_name || ""}`.replace(
        /\s+/g,
        "_",
      )
    : "patient";
  const ver = s?.version_no ? `v${s.version_no}` : "v1";
  return `consent_${name}_${ver}.pdf`;
}
